import { useMemo } from "react";
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Divider,
    Link,
    Sheet,
    Typography,
} from "@mui/joy";
import { ChevronRight, ExternalLink, Plug, Settings2 } from "lucide-react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { Button, Alert } from "../../shared";
import { useAuth, useApiResponseParser } from "../../hooks";
import { useIntegration } from "../../hooks/query";
import {
    INTEGRATIONS,
    isOrganizationIntegrationActive,
    type IntegrationCategory,
    type IntegrationDefinition,
    type IntegrationStatus,
} from "./integrations";

function Breadcrumb() {
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 0, ml: 1, mr: 2 }}
        >
            <Breadcrumbs separator={<ChevronRight size={18} />}>
                <Link
                    component={RouterLink}
                    to="/admin"
                    level="title-lg"
                    sx={{
                        fontWeight: 500,
                        textDecoration: "none",
                        color: "inherit",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    Admin
                </Link>
                <Typography level="body-md" sx={{ fontWeight: 500 }}>
                    Integrations
                </Typography>
            </Breadcrumbs>
        </Box>
    );
}

function StatusChip({ status }: { status: IntegrationStatus }) {
    if (status === "connected") {
        return (
            <Chip size="sm" variant="soft" color="success">
                Connected
            </Chip>
        );
    }
    return (
        <Chip size="sm" variant="soft" color="neutral">
            Not configured
        </Chip>
    );
}

function IntegrationCard({
    integration,
    status,
}: {
    integration: IntegrationDefinition;
    status: IntegrationStatus;
}) {
    const navigate = useNavigate();

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: "4px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                height: "100%",
                bgcolor: "background.surface",
            }}
        >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "4px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "background.level1",
                        flexShrink: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        component="img"
                        src={integration.icon}
                        alt=""
                        sx={{ width: 28, height: 28, objectFit: "contain" }}
                    />
                </Box>
                <Box flex={1} minWidth={0}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        mb={0.5}
                    >
                        <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                            {integration.name}
                        </Typography>
                        <StatusChip status={status} />
                    </Box>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {integration.category}
                    </Typography>
                </Box>
            </Box>

            <Typography level="body-sm" sx={{ color: "text.secondary", flex: 1 }}>
                {integration.description}
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                    size="sm"
                    variant="soft"
                    color="primary"
                    startDecorator={<Settings2 size={14} />}
                    onClick={() =>
                        navigate(`/admin/integrations/${integration.id}`)
                    }
                >
                    Configure
                </Button>
                {integration.docsUrl ? (
                    <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        component="a"
                        href={integration.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        endDecorator={<ExternalLink size={14} />}
                    >
                        Docs
                    </Button>
                ) : null}
            </Box>
        </Sheet>
    );
}

export default function Integrations() {
    const { selectedOrganization } = useAuth();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useList } = useIntegration();
    const { data, isLoading, isError, error } = useList(selectedOrganization?.Id);

    const statusById = useMemo(() => {
        const map = new Map<string, IntegrationStatus>();
        for (const item of INTEGRATIONS) {
            map.set(item.id, "not_configured");
        }
        for (const row of data || []) {
            map.set(
                row.IntegrationId,
                isOrganizationIntegrationActive(row.IsActive)
                    ? "connected"
                    : "not_configured",
            );
        }
        return map;
    }, [data]);

    const categories = useMemo(() => {
        const map = new Map<IntegrationCategory, IntegrationDefinition[]>();
        for (const item of INTEGRATIONS) {
            const list = map.get(item.category) || [];
            list.push(item);
            map.set(item.category, list);
        }
        return Array.from(map.entries());
    }, []);

    if (!selectedOrganization?.Id) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Select an organization to manage integrations." />
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage={parseErrorResponseData(error) || "Failed to load integrations."} />
            </Box>
        );
    }

    return (
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            maxHeight="100%"
            sx={{ overflow: "hidden", minWidth: 0, minHeight: 0 }}
        >
            <Breadcrumb />
            <Divider sx={{ mt: 0, ml: 0 }} />

            <Box flex={1} sx={{ minWidth: 0, overflow: "auto" }}>
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1, maxWidth: 960 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Plug size={18} />
                        <Typography level="title-lg">Integrations</Typography>
                    </Box>
                    <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                        Connect measurement and e-signature providers for{" "}
                        {selectedOrganization.Name}. Credentials are stored securely per
                        organization.
                    </Typography>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 960 }}>
                    {categories.map(([category, items]) => (
                        <Box key={category} mb={3.5}>
                            <Typography
                                level="body-xs"
                                sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    fontWeight: 700,
                                    color: "text.tertiary",
                                    mb: 1.25,
                                }}
                            >
                                {category}
                            </Typography>
                            <Box
                                display="grid"
                                gap={2}
                                sx={{
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "1fr 1fr",
                                    },
                                }}
                            >
                                {items.map((integration) => (
                                    <IntegrationCard
                                        key={integration.id}
                                        integration={integration}
                                        status={
                                            statusById.get(integration.id) ||
                                            "not_configured"
                                        }
                                    />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
